
import React, { createContext, useContext, useState, useCallback, useEffect } from "react";

interface SelectionContextType {
  selectedIds: string[];
  isSelected: (id: string) => boolean;
  toggleSelection: (id: string, multiSelect?: boolean, rangeSelect?: boolean) => void;
  selectRange: (startId: string, endId: string, allIds: string[]) => void;
  selectAll: (ids: string[]) => void;
  clearSelection: () => void;
  selectedCount: number;
  lastSelectedId: string | null; // Added to expose this property in the context
}

const SelectionContext = createContext<SelectionContextType>({
  selectedIds: [],
  isSelected: () => false,
  toggleSelection: () => {},
  selectRange: () => {},
  selectAll: () => {},
  clearSelection: () => {},
  selectedCount: 0,
  lastSelectedId: null, // Added default value
});

export const useSelection = () => useContext(SelectionContext);

interface SelectionProviderProps {
  children: React.ReactNode;
}

export const SelectionProvider: React.FC<SelectionProviderProps> = ({ children }) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null);

  // Handle escape key to clear selection
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Clear selection on Escape
      if (e.key === "Escape") {
        clearSelection();
      }
      
      // Select all on Cmd/Ctrl+A
      if ((e.metaKey || e.ctrlKey) && e.key === "a" && document.activeElement?.tagName !== "INPUT") {
        e.preventDefault();
        // The actual selection happens in the content display component
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const isSelected = useCallback((id: string) => {
    return selectedIds.includes(id);
  }, [selectedIds]);

  const toggleSelection = useCallback((id: string, multiSelect = false, rangeSelect = false) => {
    setSelectedIds(prev => {
      // If multiSelect (Ctrl/Cmd+click)
      if (multiSelect) {
        const isCurrentlySelected = prev.includes(id);
        if (isCurrentlySelected) {
          // Remove from selection
          const result = prev.filter(selectedId => selectedId !== id);
          setLastSelectedId(result.length > 0 ? result[result.length - 1] : null);
          return result;
        } else {
          // Add to selection
          setLastSelectedId(id);
          return [...prev, id];
        }
      } 
      // If single select (normal click)
      else if (!rangeSelect) {
        setLastSelectedId(id);
        return [id];
      }
      
      // rangeSelect is handled separately in selectRange
      return prev;
    });
  }, []);

  const selectRange = useCallback((startId: string, endId: string, allIds: string[]) => {
    if (!startId || !allIds.length) return;
    
    const startIndex = allIds.indexOf(startId);
    const endIndex = allIds.indexOf(endId);
    
    if (startIndex === -1 || endIndex === -1) return;
    
    const start = Math.min(startIndex, endIndex);
    const end = Math.max(startIndex, endIndex);
    
    const rangeIds = allIds.slice(start, end + 1);
    setSelectedIds(rangeIds);
    setLastSelectedId(endId);
  }, []);

  const selectAll = useCallback((ids: string[]) => {
    setSelectedIds(ids);
    setLastSelectedId(ids.length > 0 ? ids[ids.length - 1] : null);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds([]);
    setLastSelectedId(null);
  }, []);

  const value = {
    selectedIds,
    isSelected,
    toggleSelection,
    selectRange,
    selectAll,
    clearSelection,
    selectedCount: selectedIds.length,
    lastSelectedId, // Now exposing this in the context
  };

  return (
    <SelectionContext.Provider value={value}>
      {children}
    </SelectionContext.Provider>
  );
};

export const useContentSelection = () => {
  const selection = useSelection();
  
  // Additional helper functions specific to content can be added here
  
  return selection;
};
